import type { InferOutput } from 'valibot'
import { array, boolean, nullable, nullish, number, object, optional, pipe, string, transform } from 'valibot'
import { commonSchema } from './common'
import { lastUpdatedByEditor } from '#shared/utils/solr-fields'

// Excluded node type prefixes used for filtering
export const excludedNodeTypePrefixes = ['book_forord', 'book_nav', 'book_toc', 'book_htmltoc', 'book_innholdsfortegnelse-1']
export const hidePageNumberOnSecondLevelPageNodeType = ['book_title', 'book_colophon']

// Base Literature interface (keeping for backward compatibility)
// Example, one book, one academic article: https://ss929090-2ape21mx-eu-west-1-aws.searchstax.com/solr/?ljsdlsmwkk6exxfusyextlkw5q=OHiyhuZAoAtyuJbFiLn-uCnnlZkDrv20VsaAf1s6AysCMLZ-AIBQNMhpZGwFUnfo4i24jxyE#/juridika-content/query?q=id:isbn-9788215070551%20OR%20id:a2483222-4345-4dc5-8ac8-a6dee30f50fe&q.op=OR&defType=edismax&indent=true&rows=100&fl=id,title,nodeType,%20author,*&facet=true&facet.field=author&fq=nodeType:book%20OR%20nodeType:academic_article

/**
 * DTO interface for literature items used in UI components
 * Transforms Solr data into a consistent format for the frontend
 */
export interface LiteratureDto {
  id: string
  title: string
  authors: string[]
  legalAreas: string[]
  lastModified?: string
  edition?: string | null
  type: string
  nodeType: 'book' | 'academic_article'
  isFavorite: boolean
  coverImage?: string
  remoteId?: string
  subtitle?: string
  lastUpdatedByEditor?: string
  dateOfPublication?: string
  year?: string
}

/**
 * Schema for book and academic article documents in Solr
 */
export const bookAndArticleSchema = object({
  ...commonSchema.entries,
  nodeType: string(),
  legal_areas: optional(array(string())),

  // Book fields
  publisher: optional(array(string())),
  author: optional(array(string())),
  isbn: optional(array(string())),
  year: optional(string()),
  coverImage: optional(string()),
  dateOfPublication: optional(string()),
  description: optional(string()),
  // Note: placeOfPublication is not yet implemented in the data source
  placeOfPublication: optional(string()),
  previousEdition: optional(string()),
  subtitle: nullish(string()),
  language: optional(string()),
  editionNumber: nullish(string()),
  lastEdition: nullish(boolean()),

  // Academic articles fields  journalId: optional(string()),
  journalId: optional(string()),
  ezMainNodeId: optional(number()),
  path_location: optional(array(string())),
  authorName: optional(array(string())),
  lastUpdatedByEditor,
  remoteId: optional(string()),
  _version_: number(),
  author_list: optional(array(string())),

})

export type BookAndArticle = InferOutput<typeof bookAndArticleSchema>

/**
 * Schema for book chapter documents in Solr
 */
export const chapterSchema = object({
  ...commonSchema.entries,
  _sibling_before_: nullish(string()),
  _sibling_after_: nullish(string()),
})

export type Chapter = InferOutput<typeof chapterSchema>

/**
 * Schema for book page documents in Solr
 */
export const pageSchema = object({
  ...commonSchema.entries,
  bodytext: string(),
  nodeType: string(),
  secondLevelNodeType: optional(string()),
  pageNumber: optional(array(number())),
  pageNumberString: nullable(pipe(
    string(),
    transform(input => input === 'null' ? null : input),
  )),
  pageCounter: number(),
})

export type Page = InferOutput<typeof pageSchema>
